import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Select, Button, Space, Typography, Tag } from 'antd';
import { ReloadOutlined, ApiOutlined } from '@ant-design/icons';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dayjs from 'dayjs';
import { useLoading } from '../../context/LoadingContext';

// Register Chart.js components
Chart.register(...registerables);

// Unregister ChartDataLabels plugin globally to prevent labels from showing
Chart.unregister(ChartDataLabels);

const { Title, Text } = Typography;
const { Option } = Select;

// Types
interface DataRecord {
    timestamp: string;
    datapack_id: string;
    datapack_name: string;
    datapack_type: string;
    success_count: number;
    failure_count: number;
    unknown_result_count: number;
    total_count: number;
    success_rate: number;
    failure_rate: number;
    unknown_result_rate: number;
    unloaded_count: number;
    unloading_ratio: number;
    avg_response_time_ms: number;
    min_response_time_ms: number;
    max_response_time_ms: number;
    total_response_time_ms: number;
}

interface DataSource {
    data: DataRecord[];
}

interface MetricsQuery {
    start_time: string;
    end_time: string;
}

// API Service
class ApiService {

    async fetchMetrics(query: MetricsQuery): Promise<DataRecord[]> {
        const config = {
            baseUrl: process.env.REACT_APP_DATASPONSOR_STATISTIC_API_URL,
            secretKey: process.env.REACT_APP_DATASPONSOR_STATISTIC_SECRET_KEY || ''
        };
        const url = new URL('/api/metrics', config.baseUrl);
        url.searchParams.set('start_time', query.start_time);
        url.searchParams.set('end_time', query.end_time);

        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Secret-Api-Key': config.secretKey
                }
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status} ${response.statusText}`);
            }

            const data: DataSource = await response.json();

            if (!data.data || !Array.isArray(data.data)) {
                console.error('Invalid API response structure:', data);
                throw new Error('Invalid API response structure');
            }

            console.log(`✅ Fetched ${data.data.length} records`);
            return data.data;

        } catch (error) {
            console.error('❌ API fetch error:', error);
            throw error;
        }
    }
}

// Chart Manager
class ChartManager {
    private charts: Map<string, Chart> = new Map();
    private readonly colors = [
        '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
        '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6',
        '#a855f7', '#6366f1', '#22c55e', '#eab308', '#f43f5e'
    ];

    private getTimeBucketMinutes(range: string): number {
        switch (range) {
            case '15m': return 1;      // 1 minute buckets
            case '30m': return 2;      // 2 minute buckets
            case '1h': return 5;       // 5 minute buckets
            case '6h': return 30;      // 30 minute buckets
            case '12h': return 60;     // 1 hour buckets
            case '24h': return 60;     // 1 hour buckets
            case '3d': return 1440;    // 1 day buckets (24 * 60)
            case '7d': return 1440;    // 1 day buckets (24 * 60)
            case '1month': return 1440; // 1 day buckets (24 * 60)
            default: return 60;
        }
    }

    private roundTimeToInterval(timestamp: string, intervalMinutes: number): string {
        const date = dayjs(timestamp);

        if (intervalMinutes >= 1440) {
            // For day intervals, round to start of day
            return date.startOf('day').format('YYYY-MM-DD HH:mm');
        } else if (intervalMinutes >= 60) {
            // For hour intervals, round to start of hour
            return date.startOf('hour').format('YYYY-MM-DD HH:mm');
        } else {
            // For minute intervals, round down to the nearest interval
            const minutes = date.minute();
            const roundedMinutes = Math.floor(minutes / intervalMinutes) * intervalMinutes;
            return date.minute(roundedMinutes).second(0).millisecond(0).format('YYYY-MM-DD HH:mm');
        }
    }

    private formatLabel(timestamp: string, range: string): string {
        const date = dayjs(timestamp);
        switch (range) {
            case '3d':
            case '7d':
            case '1month':
                return date.format('MM/DD');
            default:
                return date.format('MM/DD HH:mm');
        }
    }

    private generateTimeBuckets(timeRange: string): string[] {
        const buckets: string[] = [];
        const now = dayjs();
        let startDate: dayjs.Dayjs;
        let endDate: dayjs.Dayjs;

        switch (timeRange) {
            case '3d':
                startDate = now.subtract(2, 'day').startOf('day'); // 2 days ago + today = 3 days
                endDate = now.startOf('day');
                break;
            case '7d':
                startDate = now.subtract(6, 'day').startOf('day'); // 6 days ago + today = 7 days
                endDate = now.startOf('day');
                break;
            case '1month':
                startDate = now.startOf('month');
                endDate = now.startOf('day');
                break;
            default:
                return buckets;
        }

        // Generate all days from startDate to endDate (inclusive)
        let currentDate = startDate;
        while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
            buckets.push(currentDate.format('YYYY-MM-DD HH:mm'));
            currentDate = currentDate.add(1, 'day');
        }

        return buckets;
    }

    createRequestCountChart(canvas: HTMLCanvasElement, records: DataRecord[], selectedDatapacks: string[], timeRange: string): void {
        const bucketMinutes = this.getTimeBucketMinutes(timeRange);
        const timeDataMap = new Map<string, Map<string, number>>();

        records.forEach(record => {
            const time = this.roundTimeToInterval(record.timestamp, bucketMinutes);
            if (!timeDataMap.has(time)) {
                timeDataMap.set(time, new Map());
            }
            const datapackMap = timeDataMap.get(time)!;
            const currentCount = datapackMap.get(record.datapack_id) || 0;
            datapackMap.set(record.datapack_id, currentCount + record.total_count);
        });

        // Generate all time buckets (for daily views, this ensures all days are shown)
        let sortedTimes: string[];
        if (timeRange === '3d' || timeRange === '7d' || timeRange === '1month') {
            sortedTimes = this.generateTimeBuckets(timeRange);
        } else {
            sortedTimes = Array.from(timeDataMap.keys()).sort();
        }

        const labels = sortedTimes.map(time => this.formatLabel(time, timeRange));
        const datapacks = selectedDatapacks.length > 0 ? selectedDatapacks : [];

        const datasets = datapacks.map((datapackId, index) => ({
            label: datapackId,
            data: sortedTimes.map(time => {
                const datapackMap = timeDataMap.get(time);
                return datapackMap?.get(datapackId) || 0;
            }),
            borderColor: this.colors[index % this.colors.length],
            backgroundColor: this.colors[index % this.colors.length] + '33',
            tension: 0.4,
            fill: false
        }));

        this.destroyChart('requestCountChart');
        this.charts.set('requestCountChart', new Chart(canvas, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                    legend: { labels: { color: '#333' } },
                    tooltip: { mode: 'index', intersect: false }
                },
                scales: {
                    x: { ticks: { color: '#666' }, grid: { color: '#e0e0e0' } },
                    y: { ticks: { color: '#666' }, grid: { color: '#e0e0e0' }, beginAtZero: true }
                }
            }
        }));
    }

    createSuccessRateChart(canvas: HTMLCanvasElement, records: DataRecord[], selectedDatapacks: string[], timeRange: string): void {
        const bucketMinutes = this.getTimeBucketMinutes(timeRange);
        const timeDataMap = new Map<string, Map<string, { success: number, total: number }>>();

        records.forEach(record => {
            const time = this.roundTimeToInterval(record.timestamp, bucketMinutes);
            if (!timeDataMap.has(time)) {
                timeDataMap.set(time, new Map());
            }
            const datapackMap = timeDataMap.get(time)!;
            const current = datapackMap.get(record.datapack_id) || { success: 0, total: 0 };
            datapackMap.set(record.datapack_id, {
                success: current.success + record.success_count,
                total: current.total + record.total_count
            });
        });

        // Generate all time buckets (for daily views, this ensures all days are shown)
        let sortedTimes: string[];
        if (timeRange === '3d' || timeRange === '7d' || timeRange === '1month') {
            sortedTimes = this.generateTimeBuckets(timeRange);
        } else {
            sortedTimes = Array.from(timeDataMap.keys()).sort();
        }

        const labels = sortedTimes.map(time => this.formatLabel(time, timeRange));
        const datapacks = selectedDatapacks.length > 0 ? selectedDatapacks : [];

        const datasets = datapacks.map((datapackId, index) => ({
            label: datapackId,
            data: sortedTimes.map(time => {
                const datapackMap = timeDataMap.get(time);
                const data = datapackMap?.get(datapackId);
                return data && data.total > 0 ? (data.success / data.total) * 100 : 0;
            }),
            borderColor: this.colors[index % this.colors.length],
            backgroundColor: this.colors[index % this.colors.length] + '33',
            tension: 0.4,
            fill: false
        }));

        this.destroyChart('successRateChart');
        this.charts.set('successRateChart', new Chart(canvas, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                    legend: { labels: { color: '#333' } },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            label: (context) => `${context.dataset.label}: ${context.parsed.y?.toFixed(2) ?? 0}%`
                        }
                    }
                },
                scales: {
                    x: { ticks: { color: '#666' }, grid: { color: '#e0e0e0' } },
                    y: {
                        ticks: { color: '#666', callback: (value) => `${value}%` },
                        grid: { color: '#e0e0e0' },
                        min: 0,
                        max: 100
                    }
                }
            }
        }));
    }

    createResponseTimeChart(canvas: HTMLCanvasElement, records: DataRecord[], selectedDatapacks: string[], timeRange: string): void {
        const bucketMinutes = this.getTimeBucketMinutes(timeRange);
        const timeDataMap = new Map<string, Map<string, number[]>>();

        records.forEach(record => {
            const time = this.roundTimeToInterval(record.timestamp, bucketMinutes);
            if (!timeDataMap.has(time)) {
                timeDataMap.set(time, new Map());
            }
            const datapackMap = timeDataMap.get(time)!;
            const current = datapackMap.get(record.datapack_id) || [];
            current.push(record.avg_response_time_ms);
            datapackMap.set(record.datapack_id, current);
        });

        // Generate all time buckets (for daily views, this ensures all days are shown)
        let sortedTimes: string[];
        if (timeRange === '3d' || timeRange === '7d' || timeRange === '1month') {
            sortedTimes = this.generateTimeBuckets(timeRange);
        } else {
            sortedTimes = Array.from(timeDataMap.keys()).sort();
        }

        const labels = sortedTimes.map(time => this.formatLabel(time, timeRange));
        const datapacks = selectedDatapacks.length > 0 ? selectedDatapacks : [];

        const datasets = datapacks.map((datapackId, index) => ({
            label: datapackId,
            data: sortedTimes.map(time => {
                const datapackMap = timeDataMap.get(time);
                const times = datapackMap?.get(datapackId);
                if (!times || times.length === 0) return 0;
                return times.reduce((a, b) => a + b, 0) / times.length;
            }),
            borderColor: this.colors[index % this.colors.length],
            backgroundColor: this.colors[index % this.colors.length] + '33',
            tension: 0.4,
            fill: false
        }));

        this.destroyChart('responseTimeChart');
        this.charts.set('responseTimeChart', new Chart(canvas, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                    legend: { labels: { color: '#333' } },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            label: (context) => `${context.dataset.label}: ${context.parsed.y?.toFixed(0) ?? 0}ms`
                        }
                    }
                },
                scales: {
                    x: { ticks: { color: '#666' }, grid: { color: '#e0e0e0' } },
                    y: {
                        ticks: { color: '#666', callback: (value) => `${value}ms` },
                        grid: { color: '#e0e0e0' },
                        beginAtZero: true
                    }
                }
            }
        }));
    }

    createDatapackDistributionChart(canvas: HTMLCanvasElement, records: DataRecord[]): void {
        const datapackCounts = new Map<string, number>();

        records.forEach(record => {
            const current = datapackCounts.get(record.datapack_id) || 0;
            datapackCounts.set(record.datapack_id, current + record.total_count);
        });

        const sortedDatapacks = Array.from(datapackCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 15);

        const labels = sortedDatapacks.map(([id]) => id);
        const data = sortedDatapacks.map(([, count]) => count);

        this.destroyChart('datapackDistributionChart');
        this.charts.set('datapackDistributionChart', new Chart(canvas, {
            type: 'bar',
            data: {
                labels,
                datasets: [{
                    label: 'Tổng số GD',
                    data,
                    backgroundColor: this.colors,
                    borderColor: this.colors,
                    borderWidth: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: { legend: { display: false } },
                scales: {
                    x: { ticks: { color: '#666' }, grid: { color: '#e0e0e0' } },
                    y: { ticks: { color: '#666' }, grid: { color: '#e0e0e0' }, beginAtZero: true }
                }
            }
        }));
    }

    createStatusDistributionChart(canvas: HTMLCanvasElement, records: DataRecord[]): void {
        let totalSuccess = 0;
        let totalFailure = 0;
        let totalUnknown = 0;

        records.forEach(record => {
            totalSuccess += record.success_count;
            totalFailure += record.failure_count;
            totalUnknown += record.unknown_result_count;
        });

        this.destroyChart('statusDistributionChart');
        this.charts.set('statusDistributionChart', new Chart(canvas, {
            type: 'doughnut',
            data: {
                labels: ['Thành công', 'Thất bại', 'Không xác định'],
                datasets: [{
                    data: [totalSuccess, totalFailure, totalUnknown],
                    backgroundColor: ['#10b981', '#ef4444', '#f59e0b'],
                    borderWidth: 2,
                    borderColor: '#fff'
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                    legend: { position: 'bottom', labels: { color: '#333' } },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const label = context.label || '';
                                const value = context.parsed;
                                const total = totalSuccess + totalFailure + totalUnknown;
                                const percentage = total > 0 ? ((value / total) * 100).toFixed(2) : 0;
                                return `${label}: ${value.toLocaleString()} (${percentage}%)`;
                            }
                        }
                    }
                }
            }
        }));
    }

    createResponseTimeDistributionChart(canvas: HTMLCanvasElement, records: DataRecord[]): void {
        const responseTimes: number[] = [];
        records.forEach(record => {
            responseTimes.push(record.avg_response_time_ms);
        });

        const bins = [0, 500, 1000, 1500, 2000, 2500, 3000, 5000, 10000];
        const binCounts = new Array(bins.length - 1).fill(0);
        const binLabels: string[] = [];

        for (let i = 0; i < bins.length - 1; i++) {
            binLabels.push(`${bins[i]}-${bins[i + 1]}ms`);
        }

        responseTimes.forEach(time => {
            for (let i = 0; i < bins.length - 1; i++) {
                if (time >= bins[i] && time < bins[i + 1]) {
                    binCounts[i]++;
                    break;
                }
            }
        });

        this.destroyChart('responseTimeDistChart');
        this.charts.set('responseTimeDistChart', new Chart(canvas, {
            type: 'bar',
            data: {
                labels: binLabels,
                datasets: [{
                    label: 'Số lượng GD',
                    data: binCounts,
                    backgroundColor: '#3b82f6',
                    borderColor: '#3b82f6',
                    borderWidth: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: { legend: { display: false } },
                scales: {
                    x: { ticks: { color: '#666' }, grid: { color: '#e0e0e0' } },
                    y: { ticks: { color: '#666' }, grid: { color: '#e0e0e0' }, beginAtZero: true }
                }
            }
        }));
    }

    destroyChart(chartId: string): void {
        const chart = this.charts.get(chartId);
        if (chart) {
            chart.destroy();
            this.charts.delete(chartId);
        }
    }

    destroyAllCharts(): void {
        this.charts.forEach(chart => chart.destroy());
        this.charts.clear();
    }

    private getUniqueDatapacks(records: DataRecord[]): string[] {
        const datapacks = new Set<string>();
        records.forEach(record => datapacks.add(record.datapack_id));
        return Array.from(datapacks).sort();
    }
}

// Main Component
export default function DataSponsorStatistics() {
    const [allRecords, setAllRecords] = useState<DataRecord[]>([]);
    const [filteredRecords, setFilteredRecords] = useState<DataRecord[]>([]);
    const [selectedDatapacks, setSelectedDatapacks] = useState<string[]>([]);
    const [timeRange, setTimeRange] = useState<string>('15m');
    const [apiStatus, setApiStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
    const [statusMessage, setStatusMessage] = useState<string>('Đang kết nối tới API...');
    const [lastUpdated, setLastUpdated] = useState<string>('-');

    const { setIsLoading } = useLoading();

    const chartManagerRef = useRef<ChartManager>(new ChartManager());
    const apiServiceRef = useRef<ApiService>(new ApiService());

    // Chart refs
    const requestCountChartRef = useRef<HTMLCanvasElement>(null);
    const successRateChartRef = useRef<HTMLCanvasElement>(null);
    const responseTimeChartRef = useRef<HTMLCanvasElement>(null);
    const datapackDistributionChartRef = useRef<HTMLCanvasElement>(null);
    const statusDistributionChartRef = useRef<HTMLCanvasElement>(null);
    const responseTimeDistChartRef = useRef<HTMLCanvasElement>(null);

    const getHoursFromRange = (range: string): number => {
        switch (range) {
            case '15m': return 0.25;
            case '30m': return 0.5;
            case '1h': return 1;
            case '6h': return 6;
            case '12h': return 12;
            case '24h': return 24;
            case '3d': return 3 * 24;
            case '7d': return 7 * 24;
            case '1month': {
                // Calculate hours from start of current month to now
                const now = dayjs();
                const startOfMonth = now.startOf('month');
                const hoursDiff = now.diff(startOfMonth, 'hour');
                return hoursDiff > 0 ? hoursDiff : 24; // At least 24 hours
            }
            default: return 24;
        }
    };

    const getTimeRange = (hoursBack: number, range: string): MetricsQuery => {
        const now = new Date();
        let startDate: Date;

        if (range === '1month') {
            // For "1month", start from the beginning of the current month
            startDate = dayjs().startOf('month').toDate();
        } else {
            // For other ranges, calculate by subtracting hours
            startDate = new Date(now.getTime() - (hoursBack * 60 * 60 * 1000));
        }

        return {
            start_time: startDate.toISOString(),
            end_time: now.toISOString()
        };
    };

    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            setApiStatus('connecting');
            setStatusMessage('Đang tải dữ liệu từ API...');

            const hoursBack = getHoursFromRange(timeRange);
            const timeQuery = getTimeRange(hoursBack, timeRange);

            console.log(`📊 Loading ${hoursBack} hours of data from API...`);
            const records = await apiServiceRef.current.fetchMetrics(timeQuery);

            setAllRecords(records);
            console.log(`✅ Loaded ${records.length} records`);

            if (records.length === 0) {
                setApiStatus('connected');
                setStatusMessage('Đã kết nối - Không có dữ liệu');
            } else {
                setApiStatus('connected');
                setStatusMessage(`Đã kết nối - Đã tải ${records.length} bản ghi`);
            }
        } catch (error) {
            console.error('❌ Failed to load data:', error);
            setApiStatus('error');
            setStatusMessage('Lỗi API - Không thể tải dữ liệu');
        } finally {
            setIsLoading(false);
        }
    }, [timeRange, setIsLoading]);

    const updateCharts = useCallback(() => {
        const records = filteredRecords.length > 0 ? filteredRecords : allRecords;

        if (records.length === 0) {
            console.warn('No records to display');
            return;
        }

        const chartManager = chartManagerRef.current;

        if (requestCountChartRef.current) {
            chartManager.createRequestCountChart(requestCountChartRef.current, records, selectedDatapacks, timeRange);
        }
        if (successRateChartRef.current) {
            chartManager.createSuccessRateChart(successRateChartRef.current, records, selectedDatapacks, timeRange);
        }
        if (responseTimeChartRef.current) {
            chartManager.createResponseTimeChart(responseTimeChartRef.current, records, selectedDatapacks, timeRange);
        }
        if (datapackDistributionChartRef.current) {
            chartManager.createDatapackDistributionChart(datapackDistributionChartRef.current, records);
        }
        if (statusDistributionChartRef.current) {
            chartManager.createStatusDistributionChart(statusDistributionChartRef.current, records);
        }
        if (responseTimeDistChartRef.current) {
            chartManager.createResponseTimeDistributionChart(responseTimeDistChartRef.current, records);
        }
    }, [allRecords, filteredRecords, selectedDatapacks, timeRange]);

    const updateMetrics = useCallback(() => {
        setLastUpdated(dayjs().format('YYYY-MM-DD HH:mm:ss'));
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (selectedDatapacks.length > 0) {
            const filtered = allRecords.filter(record => selectedDatapacks.includes(record.datapack_id));
            setFilteredRecords(filtered);
        } else {
            setFilteredRecords(allRecords);
        }
    }, [allRecords, selectedDatapacks]);

    useEffect(() => {
        updateCharts();
        updateMetrics();
    }, [filteredRecords, updateCharts, updateMetrics]);

    useEffect(() => {
        const chartManager = chartManagerRef.current;
        return () => {
            chartManager.destroyAllCharts();
        };
    }, []);

    const getUniqueDatapacks = (records: DataRecord[]): string[] => {
        const datapacks = new Set<string>();
        records.forEach(record => datapacks.add(record.datapack_id));
        return Array.from(datapacks).sort();
    };

    const uniqueDatapacks = getUniqueDatapacks(allRecords);

    const handleRefresh = () => {
        loadData();
    };

    // Calculate metrics
    const records = filteredRecords.length > 0 ? filteredRecords : allRecords;
    const totalRequests = records.reduce((sum, r) => sum + r.total_count, 0);
    const totalSuccess = records.reduce((sum, r) => sum + r.success_count, 0);
    const successRate = totalRequests > 0 ? (totalSuccess / totalRequests * 100).toFixed(2) : '0.00';
    const totalResponseTime = records.reduce((sum, r) => sum + r.avg_response_time_ms, 0);
    const avgResponseTime = records.length > 0 ? (totalResponseTime / records.length).toFixed(0) : '0';
    const activeDatapacks = getUniqueDatapacks(records).length;

    return (
        <div>
            {/* Header */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 20,
                padding: '16px 0',
                background: '#fff',
                borderRadius: 8
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        📊 Thống kê Data Sponsor
                    </Title>
                    <Tag
                        icon={<ApiOutlined />}
                        color={apiStatus === 'connected' ? 'success' : apiStatus === 'error' ? 'error' : 'processing'}
                    >
                        {statusMessage}
                    </Tag>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    flexWrap: 'wrap',
                    gap: 20,
                    width: '100%',
                }}>
                    <Space direction="vertical" style={{ flex: 1, minWidth: 200 }}>
                        <Text strong>Chọn gói dữ liệu:</Text>
                        <Select
                            mode="multiple"
                            placeholder="Chọn gói dữ liệu"
                            style={{ width: '100%' }}
                            value={selectedDatapacks}
                            onChange={setSelectedDatapacks}
                            maxTagCount="responsive"
                        >
                            {uniqueDatapacks.map(id => (
                                <Option key={id} value={id}>{id}</Option>
                            ))}
                        </Select>
                    </Space>
                    <Space direction="vertical" style={{ width: 200 }}>
                        <Text strong>Khoảng thời gian:</Text>
                        <Select
                            style={{ width: '100%' }}
                            value={timeRange}
                            onChange={setTimeRange}
                        >
                            <Option value="15m">15 phút trước</Option>
                            <Option value="30m">30 phút trước</Option>
                            <Option value="1h">1 giờ trước</Option>
                            <Option value="6h">6 giờ trước</Option>
                            <Option value="12h">12 giờ trước</Option>
                            <Option value="24h">24 giờ trước</Option>
                            <Option value="3d">3 ngày trước</Option>
                            <Option value="7d">7 ngày trước</Option>
                            <Option value="1month">Từ đầu tháng đến nay</Option>
                        </Select>
                    </Space>
                    <Space style={{ marginTop: 28 }}>
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={handleRefresh}
                        >
                            Làm mới
                        </Button>
                    </Space>
                </div>
            </div>

            {/* Metrics Cards */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 40,
                marginBottom: 40,
                flexWrap: 'wrap'
            }}>
                <div style={{
                    textAlign: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    border: '2px dashed #1890ff',
                    padding: '12px 20px',
                    borderRadius: 10,
                    minWidth: 200
                }}>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Tổng số yêu cầu</div>
                    <div style={{ fontSize: 24, color: '#1890ff' }}>{totalRequests.toLocaleString()}</div>
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    border: '2px dashed #52c41a',
                    padding: '12px 20px',
                    borderRadius: 10,
                    minWidth: 200
                }}>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Tỷ lệ thành công</div>
                    <div style={{ fontSize: 24, color: '#52c41a' }}>{successRate}%</div>
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    border: '2px dashed #1890ff',
                    padding: '12px 20px',
                    borderRadius: 10,
                    minWidth: 200
                }}>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Thời gian phản hồi TB</div>
                    <div style={{ fontSize: 24, color: '#1890ff' }}>{avgResponseTime}ms</div>
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    border: '2px dashed #722ed1',
                    padding: '12px 20px',
                    borderRadius: 10,
                    minWidth: 200
                }}>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Số gói dữ liệu</div>
                    <div style={{ fontSize: 24, color: '#722ed1' }}>{activeDatapacks}</div>
                </div>
            </div>

            {/* Charts */}
            <div style={{ marginBottom: 40 }}>
                <Card title="📈 Số lượng giao dịch theo thời gian">
                    <canvas ref={requestCountChartRef} style={{ maxHeight: 300 }} />
                </Card>
            </div>

            <div style={{ marginBottom: 40 }}>
                <Card title="✅ Tỷ lệ thành công theo thời gian">
                    <canvas ref={successRateChartRef} style={{ maxHeight: 300 }} />
                </Card>
            </div>

            <div style={{ marginBottom: 40 }}>
                <Card title="⚡ Thời gian phản hồi theo thời gian">
                    <canvas ref={responseTimeChartRef} style={{ maxHeight: 300 }} />
                </Card>
            </div>

            <div style={{ marginBottom: 40 }}>
                <Card title="📊 Tổng số giao dịch theo gói">
                    <canvas ref={datapackDistributionChartRef} style={{ maxHeight: 300 }} />
                </Card>
            </div>

            <div style={{ marginBottom: 40 }}>
                <Card title="🔢 Phân bổ trạng thái giao dịch">
                    <canvas ref={statusDistributionChartRef} style={{ maxHeight: 300 }} />
                </Card>
            </div>

            <div style={{ marginBottom: 40 }}>
                <Card title="⏱️ Phân bổ thời gian phản hồi">
                    <canvas ref={responseTimeDistChartRef} style={{ maxHeight: 300 }} />
                </Card>
            </div>

            {/* Footer */}
            <div style={{
                textAlign: 'center',
                padding: 16,
                background: '#fff',
                borderRadius: 8,
                marginTop: 40
            }}>
                <Text type="secondary">Cập nhật lần cuối: <Text strong style={{ color: '#1890ff' }}>{lastUpdated}</Text></Text>
            </div>
        </div>
    );
}
